import { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, AlertTriangle, AlertCircle, Shield, Loader2 } from 'lucide-react';
import StatusBadge from '../../../components/common/StatusBadge';
import ConfigConfirmation from '../../../components/edges/ConfigConfirmation';
import edgeService from '../../../services/edge.service';
import { useTenantStore } from '../../../stores/tenantStore';
import vppMonitoringService from '../../../services/vppMonitoring.service';
import suricataService from '../../../services/suricata.service';
import type { SuricataConfig, SuricataStatus } from '../../../services/suricata.service';
import { getSubscriptionTypeBadge } from '../../../utils/subscriptionUtils';
import { useStaleStatusRefresh } from '../../../hooks/useStaleStatusRefresh';

interface ConfigurationSummaryProps {
  edgeId: string;
  onRefreshReady?: (fn: () => void) => void;
  // VSNP-700 follow-up: forwarded to ConfigConfirmation so the offline
  // banner appears alongside the "last confirmation" snapshot.
  isOffline?: boolean;
  // VSNP-700 follow-up: when the edge is online, the stale-status hook
  // pokes the agent for a fresh inform if the cached status is older
  // than ~30s, and surfaces a catch-up banner while waiting.
  isOnline?: boolean;
}

const ConfigurationSummary = ({ edgeId, onRefreshReady, isOffline = false, isOnline = false }: ConfigurationSummaryProps) => {
  const selectedTenantId = useTenantStore((s) => s.selectedTenantId);

  // Data State
  const [physicalInterfaces, setPhysicalInterfaces] = useState<any[]>([]);
  const [logicalInterfaces, setLogicalInterfaces] = useState<any[]>([]);
  const [staticRoutes, setStaticRoutes] = useState<any[]>([]);
  const [serviceRoutes, setServiceRoutes] = useState<any[]>([]);
  const [arpTable, setArpTable] = useState<any[]>([]);
  const [subscriptions, setSubscriptions] = useState<any[]>([]);
  const [vppStatus, setVppStatus] = useState<any>(null);
  const [vppMode, setVppMode] = useState<string | null>(null);
  const [suricataConfig, setSuricataConfig] = useState<SuricataConfig | null>(null);
  const [suricataStatus, setSuricataStatus] = useState<SuricataStatus | null>(null);
  const [flowprobeConfig, setFlowprobeConfig] = useState<any>(null);

  // UI State
  const [expandedSections, setExpandedSections] = useState({
    interfaces: true,
    routing: true,
    subscriptions: true,
  });

  // VSNP-700: two-phase fetch.
  //
  // Phase 1 — DB-backed structure (physical, logical, routes, ARP,
  // subscriptions, edge details). All pure SQL on the orchestrator;
  // resolves in tens of ms regardless of whether the edge is online.
  // These drive the visible panels and render the moment they arrive.
  //
  // Phase 2 — telemetry / decoration (VPP status, Suricata config + status,
  // flowprobe). Best-effort; null/empty for offline edges. Fired in
  // parallel with phase 1 so it lands as soon as possible, but never
  // gates the structure rendering.
  //
  // Previously one giant Promise.all blocked every panel on the
  // slowest of the nine queries — for an offline edge that was the
  // status path with its large payload-parse cost.
  const fetchAllData = async () => {
    const fetchStructure = async () => {
      try {
        const [physical, logical, staticRoutesData, routes, arp, subs] = await Promise.all([
          edgeService.getPhysicalInterfaces(edgeId),
          edgeService.getLogicalInterfaces(edgeId),
          edgeService.listStaticRoutes(edgeId).catch(() => ({ items: [] })),
          edgeService.getServiceRoutes(edgeId),
          edgeService.getARPTable(edgeId),
          edgeService.listSubscriptions(edgeId),
        ]);

        const physicalFiltered = (physical.items || [])
          .filter((iface: any) => iface.configuredState === true)
          .sort((a: any, b: any) => {
            const nameA = a.name || '';
            const nameB = b.name || '';
            return nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: 'base' });
          });
        setPhysicalInterfaces(physicalFiltered);
        setLogicalInterfaces(logical.items || []);
        setStaticRoutes(staticRoutesData.items || []);
        setServiceRoutes(routes.items || []);
        setArpTable(arp.items || []);

        const subsData = Array.isArray(subs) ? subs : (subs.items || []);
        setSubscriptions(subsData);
      } catch (err) {
        console.error('[ConfigurationSummary] Failed to load structure:', err);
      }

      // Edge details — separate so vppMode lands even if the parallel
      // structure fetches have a partial failure.
      try {
        const edgeDetail = await edgeService.getById(edgeId);
        setVppMode((edgeDetail as any).vppMode || null);
      } catch { /* non-blocking */ }
    };

    const fetchTelemetry = async () => {
      // Each leg is independent and non-fatal — failure for an offline
      // edge is the expected case and is handled by the rendering code
      // (StatusBadge / "—" placeholders / showOfflineNotice).
      edgeService
        .getLatestStatus(edgeId)
        .then((data) => setVppStatus(data))
        .catch(() => setVppStatus(null));

      suricataService
        .getConfig(edgeId)
        .then((d) => setSuricataConfig(d?.config || null))
        .catch(() => setSuricataConfig(null));

      suricataService
        .getStatus(edgeId)
        .then((d) => setSuricataStatus(d?.status || null))
        .catch(() => setSuricataStatus(null));

      vppMonitoringService
        .getFlowprobeConfig(edgeId)
        .then((fpConfig) => setFlowprobeConfig(fpConfig))
        .catch(() => undefined);
    };

    // Kick both phases in parallel. fetchStructure is awaited so the
    // caller (refresh button, useEffect) knows when the visible
    // structure is ready; telemetry decorates the panels asynchronously.
    fetchTelemetry();
    await fetchStructure();
  };

  useEffect(() => {
    if (edgeId) {
      fetchAllData();
    }
  }, [edgeId, selectedTenantId]);

  useEffect(() => {
    if (onRefreshReady) {
      onRefreshReady(handleRefresh);
    }
  }, [onRefreshReady]);

  const handleRefresh = () => {
    fetchAllData();
  };

  // VSNP-700 follow-up: scoped refetch for the stale-status hook —
  // re-pulls only getLatestStatus instead of running the full
  // fetchAllData (six queries + edge details). The hook polls every 3 s
  // for up to 60 s, so per-cycle cost matters.
  const refetchVppStatus = () => {
    edgeService
      .getLatestStatus(edgeId)
      .then((data) => setVppStatus(data))
      .catch(() => undefined);
  };

  const showStaleBanner = useStaleStatusRefresh({
    edgeId,
    isOnline,
    receivedAt: vppStatus?._metadata?.received_at,
    refetch: refetchVppStatus,
  });

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // --- Drift Detection Helpers ---

  /**
   * TAP bridge mode (cloud VMs): only G0 maps to a real interface (tap500,
   * the WAN bridge TAP). G1..G9 don't exist — cloud VMs have a single NIC.
   * Any subscription or configuration referencing G1..GN on a TAP-mode edge
   * is inert (not drift) and we should render those rows as "not applicable"
   * rather than flagging drift.
   */
  const isTAPModeNonApplicable = (name: string): boolean => {
    if (vppMode !== 'TAP') return false;
    if (!name) return false;
    const m = name.match(/^G(\d+)$/i);
    if (!m) return false;
    return parseInt(m[1], 10) >= 1; // G1..GN — no physical LAN ports in TAP mode
  };

  const getVPPInterfaceByName = (name: string) => {
    if (!vppStatus?.vpp?.interfaces) return null;

    // TAP bridge mode (cloud VMs): G0 → tap500 (WAN bridge TAP).
    // G1..GN return null because they don't exist in TAP mode — callers
    // must use isTAPModeNonApplicable() to distinguish "drift" from
    // "not applicable" for those rows.
    if (vppMode === 'TAP') {
      if (name === 'G0' || name?.toUpperCase() === 'G0') {
        return vppStatus.vpp.interfaces.find((iface: any) => iface.name === 'tap500');
      }
      if (isTAPModeNonApplicable(name)) {
        return null;
      }
      // Non-physical names (wg0, wg1, tap400, loop0, etc.) — look up literal.
      return vppStatus.vpp.interfaces.find((iface: any) => iface.name === name);
    }

    // AF_PACKET mode: port names (G0, G1) don't match VPP names (host-enp1s0)
    // Map by position: af-packet interfaces are in creation order (G0, G1, G2...)
    if (vppMode === 'AF_PACKET') {
      const match = name.match(/^G(\d+)$/i);
      if (match) {
        const portIndex = parseInt(match[1], 10);
        const afPacketIfaces = vppStatus.vpp.interfaces.filter(
          (iface: any) => iface.type === 'af-packet'
        );
        return afPacketIfaces[portIndex] || null;
      }
    }

    // DPDK mode: direct name match or GigabitEthernet pattern
    return vppStatus.vpp.interfaces.find((iface: any) =>
      iface.name === name || iface.name === `GigabitEthernet0/${name.replace('G', '')}/0`
    );
  };

  /**
   * Translate a user-configured interface name (G0, G1, wg0, etc.) to the
   * VPP-reported interface name so it can be matched against VPP status
   * data (e.g. `nat_status.inside_interfaces`). Mirrors the agent-side
   * ifconfig.TranslateInterfaceName() semantics.
   *
   * Returns null if the name is non-applicable in the current mode
   * (e.g. G1..GN on a TAP-mode edge).
   */
  const translateConfiguredToVPP = (name: string): string | null => {
    if (!name) return null;
    if (isTAPModeNonApplicable(name)) return null;
    if (vppMode === 'TAP') {
      if (name === 'G0' || name.toUpperCase() === 'G0') return 'tap500';
      return name; // wg0, wg1, tap400, etc. pass through
    }
    if (vppMode === 'AF_PACKET') {
      const m = name.match(/^G(\d+)$/i);
      if (m) {
        const portIndex = parseInt(m[1], 10);
        const afPacketIfaces = (vppStatus?.vpp?.interfaces || []).filter(
          (i: any) => i.type === 'af-packet'
        );
        return afPacketIfaces[portIndex]?.name || null;
      }
      return name;
    }
    // DPDK / unknown: identity
    return name;
  };

  // Get actual operational state from VPP status
  const getVPPOperationalState = (name: string): string | null => {
    const vppIface = getVPPInterfaceByName(name);
    if (!vppIface) return null;
    // VPP uses lowercase 'up'/'down' for state
    return vppIface.state?.toLowerCase() === 'up' ? 'Up' : 'Down';
  };

  // Get link speed from VPP status
  const getVPPLinkSpeed = (name: string): string | null => {
    const vppIface = getVPPInterfaceByName(name);
    if (!vppIface || !vppIface.link_speed) return null;
    // Convert link_speed (in Kbps) to human readable format
    const speedKbps = vppIface.link_speed;
    if (speedKbps >= 1000000) {
      return `${speedKbps / 1000000} Gbps`;
    } else if (speedKbps >= 1000) {
      return `${speedKbps / 1000} Mbps`;
    }
    return `${speedKbps} Kbps`;
  };

  // Check if a static route exists in VPP FIB
  const hasStaticRouteDrift = (configuredRoute: any): boolean => {
    if (!vppStatus?.vpp?.routes) return false; // No data = can't detect drift

    // Format destination from configured route
    const destIp = configuredRoute.target_ip || configuredRoute.ip || '';
    const mask = configuredRoute.ip_mask || '';
    const configuredDest = destIp.includes('/') ? destIp :
      (mask.startsWith('/') ? `${destIp}${mask}` : `${destIp}${mask}`);
    const configuredGateway = configuredRoute.next_hop_ip || '';

    // Search for matching route in VPP routes
    const vppRoute = vppStatus.vpp.routes?.find((route: any) => {
      const vppDest = route.dst_network || route.destination || '';
      // VPP status uses 'next_hop', API uses 'next_hop_addr' or 'gateway'
      const vppGateway = route.next_hop || route.next_hop_addr || route.gateway || '';

      // Normalize and compare (case-insensitive, trim whitespace)
      return vppDest.trim().toLowerCase() === configuredDest.trim().toLowerCase() &&
        vppGateway.trim() === configuredGateway.trim();
    });

    return !vppRoute; // Drift if route NOT found in VPP
  };

  // Check if we have route data available from VPP
  const hasVPPRouteData = vppStatus?.vpp?.routes !== undefined;

  // Check if a VPP interface is in L2 bridge domain mode (no IP, used for bridging)
  const isVPPInterfaceInBridgeMode = (vppIface: any) => {
    // Bridge domain members are L2 interfaces with no IP addresses
    // They have type "dpdk" or "af-packet" but no ip_addresses array
    return vppIface &&
      (!vppIface.ip_addresses || vppIface.ip_addresses.length === 0) &&
      (vppIface.type === 'dpdk' || vppIface.type === 'af-packet');
  };

  // Find if an IP exists on any BVI/loopback interface (used in bridge domain configs)
  const findIPOnBVI = (ip: string) => {
    if (!vppStatus?.vpp?.interfaces) return null;
    return vppStatus.vpp.interfaces.find((iface: any) =>
      iface.type === 'Loopback' &&
      iface.ip_addresses?.some((addr: string) => addr === ip)
    );
  };

  const hasPhysicalInterfaceDrift = (configuredIface: any) => {
    if (!vppStatus) return false;
    // TAP mode: G1..GN don't exist on cloud VMs — render as not-applicable,
    // not as drift. The configured row is a harmless leftover, not a fault.
    if (isTAPModeNonApplicable(configuredIface.name)) return false;
    const vppIface = getVPPInterfaceByName(configuredIface.name);
    if (!vppIface) return true; // Interface exists in config but not in VPP

    // Compare admin state (configured enabled) vs VPP admin_state
    // This is more reliable than comparing operational state which depends on link status
    const configuredEnabled = configuredIface.configuredState === true;
    const vppAdminUp = vppIface.admin_state?.toLowerCase() === 'up';

    // Only report drift if admin states don't match
    // Link state (up/down) can differ due to cable being unplugged - that's not config drift
    return configuredEnabled !== vppAdminUp;
  };

  const hasLogicalInterfaceDrift = (configuredIface: any) => {
    if (!vppStatus) return false;
    // TAP mode: logical interfaces bound to G1..GN are not applicable.
    if (isTAPModeNonApplicable(configuredIface.physicalInterface)) return false;
    const vppIface = getVPPInterfaceByName(configuredIface.physicalInterface);
    if (!vppIface) return true;

    // Skip drift detection for DHCP interfaces
    // DHCP interfaces have no configured IP (ipv4 is empty/null) - IP is assigned dynamically
    // VPP will have the DHCP-allocated IP, which is correct behavior, not drift
    if (configuredIface.dhcpClient || !configuredIface.ipv4 || configuredIface.ipv4 === '-') {
      // For DHCP: if VPP has any IP, it's working correctly (DHCP allocated it)
      // If VPP has no IP, DHCP hasn't allocated yet (not drift, just pending)
      return false;
    }

    // For static IP: check IP address mismatch
    const configuredIp = configuredIface.cidr ? `${configuredIface.ipv4}/${configuredIface.cidr}` : configuredIface.ipv4;
    const vppHasIp = vppIface.ip_addresses?.some((ip: string) => ip === configuredIp);

    if (vppHasIp) {
      return false; // IP found on the physical interface - no drift
    }

    // Check if interface is in bridge domain mode (L2, no IP)
    // In bridge domain setups, the IP is on the BVI (loopback), not the physical interface
    if (isVPPInterfaceInBridgeMode(vppIface)) {
      // Check if the configured IP exists on a BVI/loopback interface
      const bviWithIp = findIPOnBVI(configuredIp);
      if (bviWithIp) {
        // IP is on BVI - this is correct for bridge domain configuration
        return false;
      }
    }

    return true; // IP not found anywhere - drift detected
  };

  // Get DHCP-allocated IP from VPP status for a logical interface
  const getDHCPAllocatedIP = (configuredIface: any): string | null => {
    if (!vppStatus) return null;
    const vppIface = getVPPInterfaceByName(configuredIface.physicalInterface);
    if (!vppIface || !vppIface.ip_addresses || vppIface.ip_addresses.length === 0) return null;
    return vppIface.ip_addresses[0]; // Return first IP (DHCP-allocated)
  };

  // --- Subscription Drift Detection Helpers ---

  /**
   * Check if VPP status has data for a specific subscription type
   */
  const hasVPPDataForSubscriptionType = (subscriptionType: string): boolean => {
    if (!vppStatus?.vpp) return false;

    const type = subscriptionType?.toUpperCase() || '';
    switch (type) {
      case 'DHCP_SERVER':
      case 'DHCP':
      case 'DHCP_POOL':
        return !!vppStatus.vpp.dhcp_server;

      case 'SNAT':
      case 'DNAT':
      case 'NAT':
      case 'PORT_FORWARD':
      case 'PORT_FORWARDING':
        return !!vppStatus.vpp.nat_status;

      case 'WIREGUARD':
      case 'WG':
      case 'WIREGUARD_APPS':
      case 'WG_APPS':
      case 'VPN':
        return !!vppStatus.vpp.wireguard;

      case 'ACL':
      case 'FIREWALL':
        return !!vppStatus.vpp.acl;

      default:
        return false;
    }
  };

  /**
   * Map physical interface name (G0, G1, G2) to LCP TAP interface name (lan0, lan1, lan2)
   * The edge agent creates LCP pairs with naming: GX -> lanX
   */
  const physicalToLcpInterface = (physicalName: string): string | null => {
    if (!physicalName) return null;
    // Match G0, G1, G2, etc. pattern
    const match = physicalName.match(/^G(\d+)$/i);
    if (match) {
      return `lan${match[1]}`;
    }
    return null;
  };

  /**
   * Get subscription sync status based on VPP status
   */
  const getSubscriptionSyncStatus = (subscription: any): { status: 'synced' | 'drift' | 'unknown'; reason?: string } => {
    if (!vppStatus?.vpp) {
      return { status: 'unknown', reason: 'No VPP status available' };
    }

    const type = subscription.type?.toUpperCase() || '';

    // DHCP Server
    if (type === 'DHCP_SERVER' || type === 'DHCP' || type === 'DHCP_POOL') {
      const dhcpServer = vppStatus.vpp.dhcp_server;
      if (!dhcpServer) {
        return { status: 'unknown', reason: 'No DHCP server status in VPP payload' };
      }
      if (!dhcpServer.enabled) {
        return { status: 'drift', reason: 'DHCP server not enabled' };
      }
      if (!dhcpServer.running) {
        return { status: 'drift', reason: 'DHCP server not running' };
      }
      // Check if pool exists for the interface
      // The subscription may have logical interface name (LAN-Ethernet-B) or physical (G2)
      // VPP status has LCP TAP interface name (lan2)
      const configuredIface = subscription.logicalInterface || subscription.interface;
      if (configuredIface && dhcpServer.pools) {
        // Try to get the physical interface name from the logical interface
        // logicalInterfaces has: { name: "LAN-Ethernet-B", physicalInterface: "G2", ... }
        const logicalPort = logicalInterfaces.find((li: any) =>
          li.name === configuredIface || li.physicalInterface === configuredIface
        );
        const physicalIface = logicalPort?.physicalInterface || configuredIface;

        // Map physical interface (G2) to LCP TAP name (lan2)
        const lcpIface = physicalToLcpInterface(physicalIface);

        const matchingPool = dhcpServer.pools.find((p: any) => {
          const poolIface = p.interface?.toLowerCase();
          const configLower = configuredIface?.toLowerCase();
          const physicalLower = physicalIface?.toLowerCase();
          const lcpLower = lcpIface?.toLowerCase();

          return poolIface === configLower ||
            poolIface === physicalLower ||
            poolIface === lcpLower ||
            poolIface?.includes(configLower);
        });
        if (!matchingPool) {
          return { status: 'drift', reason: `No pool for ${configuredIface} (expected LCP: ${lcpIface || 'unknown'})` };
        }
      }
      return { status: 'synced' };
    }

    // SNAT
    if (type === 'SNAT' || type === 'NAT') {
      const natStatus = vppStatus.vpp.nat_status;
      if (!natStatus) {
        return { status: 'unknown', reason: 'No NAT status in VPP payload' };
      }
      if (!natStatus.enabled) {
        return { status: 'drift', reason: 'NAT not enabled' };
      }
      return { status: 'synced' };
    }

    // DNAT / Port Forward / Port Forwarding
    if (type === 'DNAT' || type === 'PORT_FORWARD' || type === 'PORT_FORWARDING') {
      const natStatus = vppStatus.vpp.nat_status;
      if (!natStatus) {
        return { status: 'unknown', reason: 'No NAT status in VPP payload' };
      }
      if (!natStatus.enabled) {
        return { status: 'drift', reason: 'NAT not enabled' };
      }
      // Check if DNAT rule exists — port_forwarding subs store config in nested configuration object
      const config = subscription.configuration || {};
      const externalPort = config.external_port;
      const internalIp = config.internal_ip;
      if (externalPort && natStatus.dnat_rules) {
        const portNum = typeof externalPort === 'string' ? parseInt(externalPort, 10) : externalPort;
        const matchingRule = natStatus.dnat_rules.find((r: any) =>
          r.external_port === portNum
        );
        if (!matchingRule) {
          return { status: 'drift', reason: `DNAT rule for port ${externalPort}${internalIp ? ` → ${internalIp}` : ''} not found` };
        }
      }
      return { status: 'synced' };
    }

    // WireGuard
    if (type === 'WIREGUARD' || type === 'WG' || type === 'VPN') {
      const wgStatus = vppStatus.vpp.wireguard;
      if (!wgStatus || !wgStatus.interfaces?.length) {
        return { status: 'unknown', reason: 'No WireGuard status in VPP payload' };
      }
      const wg0 = wgStatus.interfaces.find((i: any) => i.name === 'wg0');
      if (!wg0) {
        return { status: 'drift', reason: 'wg0 interface not found' };
      }
      if (!wg0.enabled) {
        return { status: 'drift', reason: 'wg0 not enabled' };
      }
      return { status: 'synced' };
    }

    // WireGuard Apps (wg1)
    if (type === 'WIREGUARD_APPS' || type === 'WG_APPS') {
      const wgStatus = vppStatus.vpp.wireguard;
      if (!wgStatus || !wgStatus.interfaces?.length) {
        return { status: 'unknown', reason: 'No WireGuard status in VPP payload' };
      }
      const wg1 = wgStatus.interfaces.find((i: any) => i.name === 'wg1');
      if (!wg1) {
        return { status: 'drift', reason: 'wg1 interface not found' };
      }
      if (!wg1.enabled) {
        return { status: 'drift', reason: 'wg1 not enabled' };
      }
      return { status: 'synced' };
    }

    // ACL / Firewall
    if (type === 'ACL' || type === 'FIREWALL') {
      const aclStatus = vppStatus.vpp.acl;
      if (!aclStatus) {
        return { status: 'unknown', reason: 'No ACL status in VPP payload' };
      }
      if (subscription.name && aclStatus.rules) {
        const matchingAcl = aclStatus.rules.find((r: any) => r.name === subscription.name);
        if (!matchingAcl) {
          return { status: 'drift', reason: 'ACL rule not found' };
        }
        if (!matchingAcl.enabled) {
          return { status: 'drift', reason: 'ACL not enabled' };
        }
      }
      return { status: 'synced' };
    }

    return { status: 'unknown', reason: 'Unsupported subscription type' };
  };

  /**
   * Get the VPP Sync badge for a subscription
   */
  const getSubscriptionSyncBadge = (subscription: any) => {
    const hasVPPData = hasVPPDataForSubscriptionType(subscription.type);
    const syncStatus = getSubscriptionSyncStatus(subscription);

    if (!hasVPPData || syncStatus.status === 'unknown') {
      return (
        <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-600" title={syncStatus.reason}>
          Unknown
        </span>
      );
    }

    if (syncStatus.status === 'drift') {
      return (
        <span className="px-2 py-1 text-xs font-medium rounded-full bg-warning-100 text-warning-700 inline-flex items-center space-x-1" title={syncStatus.reason}>
          <AlertTriangle className="w-3 h-3" />
          <span>Drift</span>
        </span>
      );
    }

    return (
      <span className="px-2 py-1 text-xs font-medium rounded-full bg-success-100 text-success-700">
        Synced
      </span>
    );
  };

  // Count subscription drifts
  const subscriptionDriftCount = subscriptions.filter(sub => {
    const status = getSubscriptionSyncStatus(sub);
    return status.status === 'drift';
  }).length;

  // --- Helpers ---

  const getRouteBadgeColor = (type: string) => {
    switch (type) {
      case 'Static': return 'bg-blue-100 text-blue-700';
      case 'Dynamic': return 'bg-purple-100 text-purple-700';
      case 'Connected': return 'bg-green-100 text-green-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getARPStateBadge = (state: string) => {
    switch (state) {
      case 'Reachable': return 'bg-green-100 text-green-700';
      case 'Stale': return 'bg-yellow-100 text-yellow-700';
      case 'Incomplete': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  // Check if sections have data
  const hasPhysicalInterfaces = physicalInterfaces.length > 0;
  const hasLogicalInterfaces = logicalInterfaces.length > 0;
  const hasInterfaces = hasPhysicalInterfaces || hasLogicalInterfaces;
  const hasStaticRoutes = staticRoutes.length > 0;
  const hasRouting = staticRoutes.length > 0 || serviceRoutes.length > 0 || arpTable.length > 0;

  // Check for drift
  const physicalDriftCount = physicalInterfaces.filter(hasPhysicalInterfaceDrift).length;
  const logicalDriftCount = logicalInterfaces.filter(hasLogicalInterfaceDrift).length;
  const staticRouteDriftCount = hasVPPRouteData ? staticRoutes.filter(hasStaticRouteDrift).length : 0;
  const totalInterfaceDriftCount = physicalDriftCount + logicalDriftCount;
  const totalDriftCount = totalInterfaceDriftCount + staticRouteDriftCount;
  const hasDrift = vppStatus && totalInterfaceDriftCount > 0;
  const hasRoutingDrift = hasVPPRouteData && staticRouteDriftCount > 0;

  // Snapshot view: subscription card visibility
  const subTypes = new Set(
    subscriptions.map((s: any) => (s.type || s.subscription_type || '').toLowerCase())
  );
  const hasSnatSub = subTypes.has('source_nat') || subTypes.has('snat') || subTypes.has('nat');
  const hasDhcpSub = subTypes.has('dhcp_pool') || subTypes.has('dhcp_server') || subTypes.has('dhcp');
  const hasPortForwardingSub = subTypes.has('port_forwarding') || subTypes.has('port_forward');
  const hasSubscriptions = subscriptions.length > 0;

  // Snapshot view: security card visibility
  const showAclCard = subTypes.has('acl') || subTypes.has('firewall')
    || (vppStatus?.vpp?.acl?.rules?.length ?? 0) > 0;
  const hasFlowprobePlugin = (vppStatus?.vpp?.plugins || []).some((p: any) => p.name === 'flowprobe_plugin.so');
  const showIpfixCard = hasFlowprobePlugin || flowprobeConfig?.config?.enabled;
  const showWg0Card = subTypes.has('wireguard') || subTypes.has('wg') || subTypes.has('vpn')
    || !!vppStatus?.vpp?.wireguard?.interfaces?.find((i: any) => i.name === 'wg0');
  const showWg1Card = subTypes.has('wireguard_apps') || subTypes.has('wg_apps')
    || !!vppStatus?.vpp?.wireguard?.interfaces?.find((i: any) => i.name === 'wg1');
  const showSuricataCard = !!suricataConfig;
  const showSecuritySection = showAclCard || showIpfixCard || showWg0Card || showWg1Card || showSuricataCard;

  return (
    <div className="space-y-8">

      {/* VSNP-700 follow-up: edge is online but the cached status
          payload is stale (>30s old). The hook is auto-polling
          getLatestStatus while this banner is visible and has already
          poked the agent for a fresh inform. */}
      {showStaleBanner && (
        <div
          role="status"
          className="flex items-center gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900"
        >
          <Loader2 className="h-4 w-4 flex-shrink-0 animate-spin text-blue-600" aria-hidden="true" />
          <span>
            <span className="font-semibold">Configuration is stale.</span>{' '}
            Gathering confirmation from online edge now…
          </span>
        </div>
      )}

      {/* Configuration Confirmation Status Panel */}
      <ConfigConfirmation edgeId={edgeId} isOffline={isOffline} />

      {/* 1. Interfaces Section */}
      {hasInterfaces && (
        <div className="card">
          <div
            className="flex items-center justify-between cursor-pointer border-b pb-4 mb-4"
            onClick={() => toggleSection('interfaces')}
          >
            <div className="flex items-center space-x-3">
              <h2 className="text-xl font-bold text-gray-900">Interfaces</h2>
              {hasDrift && (
                <span className="px-2 py-1 text-xs font-medium rounded-full bg-warning-100 text-warning-700 inline-flex items-center space-x-1">
                  <AlertTriangle className="w-3 h-3" />
                  <span>{totalDriftCount} Drift{totalDriftCount > 1 ? 's' : ''}</span>
                </span>
              )}
            </div>
            {expandedSections.interfaces ? <ChevronUp className="w-5 h-5 text-gray-500" /> : <ChevronDown className="w-5 h-5 text-gray-500" />}
          </div>

          {expandedSections.interfaces && (
            <div className="space-y-6">
              {vppMode && (
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <span className="font-medium">VPP Mode:</span>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${vppMode === 'AF_PACKET' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                    }`}>
                    {vppMode}
                  </span>
                </div>
              )}
              {!vppStatus && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
                    <div>
                      <h4 className="text-sm font-semibold text-blue-900">No VPP Status Available</h4>
                      <p className="text-sm text-blue-700 mt-1">
                        Unable to detect configuration drift because no VPP status data is available.
                        The edge may not be publishing VPP status confirmations, or the VPP data collector may not be running.
                        Check the VPP Status tab for more details.
                      </p>
                    </div>
                  </div>
                </div>
              )}
              {hasDrift && (
                <div className="bg-warning-50 border border-warning-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <AlertTriangle className="w-5 h-5 text-warning-600 mt-0.5" />
                    <div>
                      <h4 className="text-sm font-semibold text-warning-900">Configuration Drift Detected</h4>
                      <p className="text-sm text-warning-700 mt-1">
                        {totalDriftCount} interface{totalDriftCount > 1 ? 's have' : ' has'} configuration drift. The VPP state does not match the configured state.
                        {physicalDriftCount > 0 && ` ${physicalDriftCount} physical interface${physicalDriftCount > 1 ? 's' : ''}.`}
                        {logicalDriftCount > 0 && ` ${logicalDriftCount} logical interface${logicalDriftCount > 1 ? 's' : ''}.`}
                      </p>
                    </div>
                  </div>
                </div>
              )}
              {/* Physical Interfaces */}
              {hasPhysicalInterfaces && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Physical Interfaces</h3>
                  <div className="overflow-x-auto border border-gray-200 rounded-lg">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Configured</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Speed</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">MAC</th>
                          {vppStatus && <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">VPP Sync</th>}
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {physicalInterfaces.length === 0 ? (
                          <tr><td colSpan={vppStatus ? 7 : 6} className="px-6 py-4 text-center text-gray-500">No physical interfaces found</td></tr>
                        ) : (
                          physicalInterfaces.map((iface) => {
                            const nonApplicable = isTAPModeNonApplicable(iface.name);
                            const hasDrift = !nonApplicable && hasPhysicalInterfaceDrift(iface);
                            // Use VPP status for actual operational state (if available)
                            const vppOpState = getVPPOperationalState(iface.name);
                            const displayState = vppOpState || iface.operationalState;
                            // Use VPP status for link speed (if available)
                            const vppSpeed = getVPPLinkSpeed(iface.name);
                            const displaySpeed = vppSpeed || iface.speed || '-';
                            return (
                              <tr key={iface.id} className={`hover:bg-gray-50 ${hasDrift ? 'bg-warning-50' : nonApplicable ? 'bg-gray-50' : ''}`}>
                                <td className="px-6 py-4 text-sm font-medium text-gray-900">{iface.name}</td>
                                <td className="px-6 py-4 text-sm text-gray-900">{iface.type}</td>
                                <td className="px-6 py-4">
                                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${iface.configuredState ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'}`}>
                                    {iface.configuredState ? 'Yes' : 'No'}
                                  </span>
                                </td>
                                <td className="px-6 py-4"><StatusBadge status={displayState} /></td>
                                <td className="px-6 py-4 text-sm text-gray-900">{displaySpeed}</td>
                                <td className="px-6 py-4 text-sm text-gray-500 font-mono">{iface.macAddress}</td>
                                {vppStatus && (
                                  <td className="px-6 py-4">
                                    {nonApplicable ? (
                                      <span
                                        className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-600"
                                        title="Single-NIC cloud VM in TAP bridge mode — LAN ports do not exist in this deployment."
                                      >
                                        N/A
                                      </span>
                                    ) : hasDrift ? (
                                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-warning-100 text-warning-700 inline-flex items-center space-x-1">
                                        <AlertTriangle className="w-3 h-3" />
                                        <span>Drift</span>
                                      </span>
                                    ) : (
                                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-success-100 text-success-700">
                                        Synced
                                      </span>
                                    )}
                                  </td>
                                )}
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Logical Interfaces */}
              {hasLogicalInterfaces && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Logical Interfaces</h3>
                  <div className="overflow-x-auto border border-gray-200 rounded-lg">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">IPv4 Address</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Physical Port</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Advanced</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                          {vppStatus && <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">VPP Sync</th>}
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {logicalInterfaces.length === 0 ? (
                          <tr><td colSpan={vppStatus ? 8 : 7} className="px-6 py-4 text-center text-gray-500">No logical interfaces found</td></tr>
                        ) : (
                          logicalInterfaces.map((iface) => {
                            const nonApplicable = isTAPModeNonApplicable(iface.physicalInterface);
                            const hasDrift = !nonApplicable && hasLogicalInterfaceDrift(iface);
                            const isDHCP = iface.dhcpClient || !iface.ipv4;
                            const dhcpAllocatedIP = isDHCP ? getDHCPAllocatedIP(iface) : null;
                            // Use VPP status for actual operational state (based on physical interface)
                            const vppOpState = getVPPOperationalState(iface.physicalInterface);
                            const displayState = vppOpState || iface.operationalState;
                            return (
                              <tr key={iface.id} className={`hover:bg-gray-50 ${hasDrift ? 'bg-warning-50' : nonApplicable ? 'bg-gray-50' : ''}`}>
                                <td className="px-6 py-4 text-sm font-medium text-gray-900">{iface.name}</td>
                                <td className="px-6 py-4"><span className={`px-2 py-1 text-xs font-medium rounded-full ${iface.role === 'WAN' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>{iface.role}</span></td>
                                <td className="px-6 py-4 text-sm text-gray-900">
                                  {iface.type}
                                  {isDHCP && (
                                    <span className="ml-2 px-1.5 py-0.5 text-xs font-medium rounded bg-purple-100 text-purple-700">DHCP</span>
                                  )}
                                </td>
                                <td className="px-6 py-4 text-sm text-gray-900 font-mono">
                                  {isDHCP ? (
                                    dhcpAllocatedIP ? (
                                      <span className="text-purple-700" title="DHCP-allocated IP from VPP">{dhcpAllocatedIP}</span>
                                    ) : (
                                      <span className="text-gray-400 italic">DHCP pending...</span>
                                    )
                                  ) : (
                                    iface.cidr ? `${iface.ipv4}/${iface.cidr}` : iface.ipv4
                                  )}
                                </td>
                                <td className="px-6 py-4 text-sm text-gray-900">{iface.physicalInterface}</td>
                                <td className="px-6 py-4 text-sm text-gray-700">
                                  {iface.vrfName && iface.vrfName !== 'default' ? (
                                    <span title={`VPP table id: ${iface.vrfTableId ?? '?'}`}>
                                      VRF: {iface.vrfName}
                                      {typeof iface.vrfTableId === 'number' && iface.vrfTableId > 0 && (
                                        <span className="text-gray-500"> ({iface.vrfTableId})</span>
                                      )}
                                    </span>
                                  ) : (
                                    <span className="text-gray-400">—</span>
                                  )}
                                </td>
                                <td className="px-6 py-4"><StatusBadge status={displayState} /></td>
                                {vppStatus && (
                                  <td className="px-6 py-4">
                                    {nonApplicable ? (
                                      <span
                                        className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-600"
                                        title="Bound to a LAN port that does not exist in single-NIC TAP bridge mode."
                                      >
                                        N/A
                                      </span>
                                    ) : hasDrift ? (
                                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-warning-100 text-warning-700 inline-flex items-center space-x-1">
                                        <AlertTriangle className="w-3 h-3" />
                                        <span>Drift</span>
                                      </span>
                                    ) : (
                                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-success-100 text-success-700">
                                        Synced
                                      </span>
                                    )}
                                  </td>
                                )}
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* 2. Routing Section */}
      {hasRouting && (
        <div className="card">
          <div
            className="flex items-center justify-between cursor-pointer border-b pb-4 mb-4"
            onClick={() => toggleSection('routing')}
          >
            <div className="flex items-center space-x-3">
              <h2 className="text-xl font-bold text-gray-900">Routing Table</h2>
              {hasRoutingDrift && (
                <span className="px-2 py-1 text-xs font-medium rounded-full bg-warning-100 text-warning-700 inline-flex items-center space-x-1">
                  <AlertTriangle className="w-3 h-3" />
                  <span>{staticRouteDriftCount} Route{staticRouteDriftCount > 1 ? 's' : ''} Drift</span>
                </span>
              )}
            </div>
            {expandedSections.routing ? <ChevronUp className="w-5 h-5 text-gray-500" /> : <ChevronDown className="w-5 h-5 text-gray-500" />}
          </div>

          {expandedSections.routing && (
            <div className="space-y-6">
              {/* Drift Warning for Routes */}
              {hasRoutingDrift && (
                <div className="bg-warning-50 border border-warning-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <AlertTriangle className="w-5 h-5 text-warning-600 mt-0.5" />
                    <div>
                      <h4 className="text-sm font-semibold text-warning-900">Static Route Drift Detected</h4>
                      <p className="text-sm text-warning-700 mt-1">
                        {staticRouteDriftCount} static route{staticRouteDriftCount > 1 ? 's are' : ' is'} configured but not found in VPP FIB.
                        This may indicate the route failed to install or was removed.
                      </p>
                    </div>
                  </div>
                </div>
              )}
              {!hasVPPRouteData && hasStaticRoutes && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
                    <div>
                      <h4 className="text-sm font-semibold text-blue-900">Route Sync Status Unknown</h4>
                      <p className="text-sm text-blue-700 mt-1">
                        No VPP route data available. The edge may not be publishing route status.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Static Routes (Configured) */}
              {hasStaticRoutes && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Static Routes (Configured)</h3>
                  <div className="overflow-x-auto border border-gray-200 rounded-lg">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Destination</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Gateway</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Interface</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Metric</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                          {hasVPPRouteData && <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">VPP Sync</th>}
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {staticRoutes.map((route) => {
                          // Format destination with CIDR
                          const destination = route.target_ip || route.ip || '';
                          const mask = route.ip_mask || '';
                          const fullDest = destination.includes('/') ? destination :
                            (mask.startsWith('/') ? `${destination}${mask}` : destination);
                          const hasDrift = hasVPPRouteData && hasStaticRouteDrift(route);

                          return (
                            <tr key={route.id} className={`hover:bg-gray-50 ${hasDrift ? 'bg-warning-50' : ''}`}>
                              <td className="px-6 py-4 text-sm font-mono text-gray-900">{fullDest || '-'}</td>
                              <td className="px-6 py-4 text-sm font-mono text-gray-900">{route.next_hop_ip || '-'}</td>
                              <td className="px-6 py-4 text-sm text-gray-900">{route.inter_name || '-'}</td>
                              <td className="px-6 py-4 text-sm text-gray-900">{route.metric || '-'}</td>
                              <td className="px-6 py-4 text-sm text-gray-500">{route.description || '-'}</td>
                              {hasVPPRouteData && (
                                <td className="px-6 py-4">
                                  {hasDrift ? (
                                    <span className="px-2 py-1 text-xs font-medium rounded-full bg-warning-100 text-warning-700 inline-flex items-center space-x-1">
                                      <AlertTriangle className="w-3 h-3" />
                                      <span>Drift</span>
                                    </span>
                                  ) : (
                                    <span className="px-2 py-1 text-xs font-medium rounded-full bg-success-100 text-success-700">
                                      Synced
                                    </span>
                                  )}
                                </td>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Service Routes */}
              <div>
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Service Routes</h3>
                <div className="overflow-x-auto border border-gray-200 rounded-lg">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Destination</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Gateway</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Interface</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Metric</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {serviceRoutes.length === 0 ? (
                        <tr><td colSpan={6} className="px-6 py-4 text-center text-gray-500">No routes found</td></tr>
                      ) : (
                        serviceRoutes.map((route) => (
                          <tr key={route.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 text-sm font-mono text-gray-900">{route.destination}/{route.subnetMask}</td>
                            <td className="px-6 py-4 text-sm font-mono text-gray-900">{route.gateway === '0.0.0.0' ? '—' : route.gateway}</td>
                            <td className="px-6 py-4 text-sm text-gray-900">{route.interface}</td>
                            <td className="px-6 py-4 text-sm text-gray-900">{route.metric}</td>
                            <td className="px-6 py-4"><span className={`px-2 py-1 text-xs font-medium rounded-full ${getRouteBadgeColor(route.type)}`}>{route.type}</span></td>
                            <td className="px-6 py-4"><span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700">{route.status}</span></td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* ARP Table */}
              <div>
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">ARP Table</h3>
                <div className="overflow-x-auto border border-gray-200 rounded-lg">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">IP Address</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">MAC Address</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Interface</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">State</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {arpTable.length === 0 ? (
                        <tr><td colSpan={4} className="px-6 py-4 text-center text-gray-500">No ARP entries found</td></tr>
                      ) : (
                        arpTable.map((entry) => (
                          <tr key={entry.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 text-sm font-mono text-gray-900">{entry.ipAddress}</td>
                            <td className="px-6 py-4 text-sm font-mono text-gray-900">{entry.macAddress}</td>
                            <td className="px-6 py-4 text-sm text-gray-900">{entry.interface}</td>
                            <td className="px-6 py-4"><span className={`px-2 py-1 text-xs font-medium rounded-full ${getARPStateBadge(entry.state)}`}>{entry.state}</span></td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* 4. Subscriptions Section */}
      {hasSubscriptions && (
        <div className="card">
          <div
            className="flex items-center justify-between cursor-pointer border-b pb-4 mb-4"
            onClick={() => toggleSection('subscriptions')}
          >
            <div className="flex items-center space-x-3">
              <h2 className="text-xl font-bold text-gray-900">Subscriptions</h2>
              {subscriptionDriftCount > 0 && (
                <span className="px-2 py-1 text-xs font-medium rounded-full bg-warning-100 text-warning-700 inline-flex items-center space-x-1">
                  <AlertTriangle className="w-3 h-3" />
                  <span>{subscriptionDriftCount} Drift{subscriptionDriftCount > 1 ? 's' : ''}</span>
                </span>
              )}
            </div>
            {expandedSections.subscriptions ? <ChevronUp className="w-5 h-5 text-gray-500" /> : <ChevronDown className="w-5 h-5 text-gray-500" />}
          </div>

          {expandedSections.subscriptions && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* NAT Configuration Card */}
              {hasSnatSub && (() => {
                const natStatus = vppStatus?.vpp?.nat_status;
                const hasNatData = !!natStatus;
                const isEnabled = natStatus?.enabled;
                const insideIfaces = natStatus?.inside_interfaces || [];
                const outsideIfaces = natStatus?.outside_interfaces || [];
                const sessionCount = natStatus?.session_count || 0;
                const addressPools = natStatus?.address_pools || [];

                // Get SNAT subscription config for comparison
                const snatSub = subscriptions.find((s: any) =>
                  s.type?.toLowerCase() === 'source_nat' || s.subscription_type?.toLowerCase() === 'source_nat'
                );
                const configuredInsideMode = snatSub?.configuration?.interface_mode || 'all';
                const configuredInsideInterfaces: string[] = snatSub?.configuration?.inside_interfaces || [];

                // Translate configured interface names (G0, G1, ...) into the VPP
                // interface names reported by nat_status (tap500, host-enp1sX, ...)
                // so we're comparing apples to apples. Non-applicable entries in
                // the current VPP mode (e.g. G1..GN on a TAP-mode edge) are
                // filtered out instead of being flagged as missing.
                const translatedConfiguredInside = configuredInsideInterfaces
                  .map((name: string) => ({
                    configured: name,
                    translated: translateConfiguredToVPP(name),
                  }));
                const applicableConfiguredInside = translatedConfiguredInside.filter(
                  (p) => p.translated !== null
                );
                const nonApplicableConfiguredInside = translatedConfiguredInside.filter(
                  (p) => p.translated === null
                );

                // Determine drift
                let syncStatus: 'synced' | 'drift' | 'unknown' = 'unknown';
                let driftReason = '';
                // In TAP bridge mode NAT is handled by Linux iptables
                // (MASQUERADE + DNAT on ens3), NOT by VPP. The VPP NAT
                // plugin is intentionally left disabled — any WG/IPSec
                // inbound port preservation depends on Linux conntrack.
                // Comparing VPP NAT state on TAP edges would always flag
                // drift. Treat as synced and surface the ownership clearly.
                const natManagedByHost = vppMode === 'TAP';
                if (natManagedByHost) {
                  syncStatus = 'synced';
                } else if (hasNatData) {
                  if (!isEnabled && snatSub) {
                    syncStatus = 'drift';
                    driftReason = 'NAT not enabled in VPP';
                  } else if (isEnabled) {
                    if (configuredInsideMode === 'specific' && applicableConfiguredInside.length > 0) {
                      const missing = applicableConfiguredInside
                        .filter((p) => !insideIfaces.includes(p.translated as string))
                        .map((p) => `${p.configured} (${p.translated})`);
                      if (missing.length > 0) {
                        syncStatus = 'drift';
                        driftReason = `Missing inside interfaces: ${missing.join(', ')}`;
                      } else {
                        syncStatus = 'synced';
                      }
                    } else {
                      // Either mode=all (VPP handles everything) or all configured
                      // insides were non-applicable in the current VPP mode —
                      // treat as synced to avoid false drift.
                      syncStatus = 'synced';
                    }
                  } else {
                    syncStatus = 'synced';
                  }
                }

                // For the "inside interfaces configured" subtitle, include a
                // note about any non-applicable names (e.g. LAN ports on a
                // TAP-mode cloud VM). This surfaces the misconfiguration
                // without flagging it as drift.
                const nonApplicableNote =
                  nonApplicableConfiguredInside.length > 0 && vppMode === 'TAP'
                    ? `${nonApplicableConfiguredInside.map((p) => p.configured).join(', ')} — not applicable (single-NIC TAP mode)`
                    : null;

                // TAP mode without VPP NAT: show a clean Linux-managed
                // card instead of empty VPP readouts (Sessions 0, Outside
                // None, Inside None, Address Pools None) which are
                // confusing to admins.
                if (natManagedByHost && !isEnabled && snatSub) {
                  return (
                    <div className="border border-gray-200 rounded-lg p-4">
                      <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3">Source NAT</h3>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Status</span>
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700">Enabled</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Mode</span>
                          <span className="text-sm font-medium text-gray-900">{(snatSub.configuration as any)?.type || 'Masquerading'}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Implementation</span>
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-700">Linux iptables</span>
                        </div>
                        <div className="text-xs text-gray-500 italic mt-1">
                          TAP bridge mode — NAT is handled by Linux MASQUERADE + conntrack on the host NIC. VPP NAT is not used; WireGuard port preservation depends on Linux conntrack.
                        </div>
                      </div>
                    </div>
                  );
                }

                return (
                  <div className="border border-gray-200 rounded-lg p-4">
                    <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3">Source NAT</h3>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Status</span>
                        {hasNatData ? (
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${isEnabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                            {isEnabled ? 'Enabled' : 'Disabled'}
                          </span>
                        ) : (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-600">Unknown</span>
                        )}
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Sessions</span>
                        <span className="text-sm font-medium text-gray-900">{sessionCount}</span>
                      </div>
                      <div className="flex items-start justify-between">
                        <span className="text-sm text-gray-600">Outside</span>
                        <div className="text-right">
                          {outsideIfaces.length > 0 ? (
                            <div className="flex flex-wrap gap-1 justify-end">
                              {outsideIfaces.map((iface: string) => (
                                <span key={iface} className="px-1.5 py-0.5 text-xs font-medium rounded bg-blue-100 text-blue-700">
                                  {iface}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-sm text-gray-500">None</span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-start justify-between">
                        <span className="text-sm text-gray-600">Inside</span>
                        <div className="text-right">
                          {insideIfaces.length > 0 ? (
                            <div className="flex flex-wrap gap-1 justify-end">
                              {insideIfaces.map((iface: string) => (
                                <span key={iface} className="px-1.5 py-0.5 text-xs font-medium rounded bg-green-100 text-green-700">
                                  {iface}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-sm text-gray-500">None</span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-start justify-between">
                        <span className="text-sm text-gray-600">Address Pools</span>
                        <div className="text-right">
                          {addressPools.length > 0 ? (
                            <div className="flex flex-wrap gap-1 justify-end">
                              {addressPools.map((pool: any, idx: number) => (
                                <span key={idx} className="px-1.5 py-0.5 text-xs font-medium rounded bg-purple-100 text-purple-700">
                                  {pool.first_ip}{pool.last_ip ? ` - ${pool.last_ip}` : ''}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-sm text-gray-500">None</span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">VPP Sync</span>
                        {syncStatus === 'synced' ? (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-success-100 text-success-700">Synced</span>
                        ) : syncStatus === 'drift' ? (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-warning-100 text-warning-700 inline-flex items-center space-x-1" title={driftReason}>
                            <AlertTriangle className="w-3 h-3" />
                            <span>Drift</span>
                          </span>
                        ) : (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-600">Unknown</span>
                        )}
                      </div>
                      {nonApplicableNote && (
                        <div className="text-xs text-gray-500 italic mt-1">
                          {nonApplicableNote}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* DHCP Server Card */}
              {hasDhcpSub && (() => {
                const dhcpStatus = vppStatus?.vpp?.dhcp_server;
                const hasDhcpData = !!dhcpStatus;
                const isEnabled = dhcpStatus?.enabled;
                const isRunning = dhcpStatus?.running;
                const pools = dhcpStatus?.pools || [];

                // Get DHCP subscription config for comparison
                const dhcpSub = subscriptions.find((s: any) =>
                  s.type?.toLowerCase() === 'dhcp_pool' || s.subscription_type?.toLowerCase() === 'dhcp_pool'
                );

                // Determine drift
                let syncStatus: 'synced' | 'drift' | 'unknown' = 'unknown';
                let driftReason = '';
                if (hasDhcpData) {
                  if (dhcpSub && !isEnabled) {
                    syncStatus = 'drift';
                    driftReason = 'DHCP server not enabled';
                  } else if (dhcpSub && !isRunning) {
                    syncStatus = 'drift';
                    driftReason = 'DHCP server not running';
                  } else if (isEnabled && isRunning) {
                    syncStatus = 'synced';
                  } else if (!dhcpSub && !isEnabled) {
                    syncStatus = 'synced'; // No DHCP configured and none enabled - OK
                  } else {
                    syncStatus = 'synced';
                  }
                }

                return (
                  <div className="border border-gray-200 rounded-lg p-4">
                    <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3">DHCP Server</h3>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Status</span>
                        {hasDhcpData ? (
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${isEnabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                            {isEnabled ? 'Enabled' : 'Disabled'}
                          </span>
                        ) : (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-600">Unknown</span>
                        )}
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Running</span>
                        {hasDhcpData ? (
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${isRunning ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                            {isRunning ? 'Yes' : 'No'}
                          </span>
                        ) : (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-600">Unknown</span>
                        )}
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Pools</span>
                        <span className="text-sm font-medium text-gray-900">{pools.length}</span>
                      </div>
                      {pools.length > 0 && (
                        <div className="space-y-2 pt-2 border-t border-gray-100">
                          {pools.map((pool: any, idx: number) => (
                            <div key={idx} className="text-xs bg-gray-50 rounded p-2">
                              <div className="flex justify-between">
                                <span className="text-gray-500">Interface:</span>
                                <span className="font-medium text-gray-700">{pool.interface || '-'}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-500">Range:</span>
                                <span className="font-mono text-gray-700">{pool.start_ip || pool.start} - {pool.end_ip || pool.end}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-500">Gateway:</span>
                                <span className="font-mono text-gray-700">{pool.gateway || pool.router || '-'}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">VPP Sync</span>
                        {syncStatus === 'synced' ? (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-success-100 text-success-700">Synced</span>
                        ) : syncStatus === 'drift' ? (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-warning-100 text-warning-700 inline-flex items-center space-x-1" title={driftReason}>
                            <AlertTriangle className="w-3 h-3" />
                            <span>Drift</span>
                          </span>
                        ) : (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-600">Unknown</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Port Forwarding Card */}
              {hasPortForwardingSub && (() => {
                const natStatus = vppStatus?.vpp?.nat_status;
                const hasNatData = !!natStatus;
                const isEnabled = natStatus?.enabled;
                const dnatRules = natStatus?.dnat_rules || [];

                const pfSubs = subscriptions.filter((s: any) => {
                  const t = (s.type || s.subscription_type || '').toLowerCase();
                  return t === 'port_forwarding' || t === 'port_forward';
                });

                // Overall sync: NAT must be enabled + all rules must exist
                let syncStatus: 'synced' | 'drift' | 'unknown' = 'unknown';
                let driftReason = '';
                if (hasNatData) {
                  if (!isEnabled) {
                    syncStatus = 'drift';
                    driftReason = 'NAT not enabled in VPP';
                  } else {
                    // Check each port forwarding rule
                    const missingRules = pfSubs.filter((s: any) => {
                      const config = s.configuration || {};
                      const extPort = config.external_port;
                      if (!extPort) return false;
                      const portNum = typeof extPort === 'string' ? parseInt(extPort, 10) : extPort;
                      return !dnatRules.find((r: any) => r.external_port === portNum);
                    });
                    if (missingRules.length > 0) {
                      syncStatus = 'drift';
                      driftReason = `${missingRules.length} DNAT rule${missingRules.length > 1 ? 's' : ''} missing`;
                    } else {
                      syncStatus = 'synced';
                    }
                  }
                }

                return (
                  <div className={`border rounded-lg p-4 ${syncStatus === 'drift' ? 'border-warning-300 bg-warning-50' : 'border-gray-200'}`}>
                    <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3">Port Forwarding (DNAT)</h3>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">NAT Status</span>
                        {hasNatData ? (
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${isEnabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                            {isEnabled ? 'Enabled' : 'Disabled'}
                          </span>
                        ) : (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-600">Unknown</span>
                        )}
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">DNAT Rules in VPP</span>
                        <span className="text-sm font-medium text-gray-900">{dnatRules.length}</span>
                      </div>
                      {pfSubs.length > 0 && (
                        <div className="space-y-2 pt-2 border-t border-gray-100">
                          {pfSubs.map((s: any) => {
                            const config = s.configuration || {};
                            const extPort = config.external_port || '-';
                            const intIp = config.internal_ip || '-';
                            const intPort = config.internal_port || extPort;
                            const protocol = (config.protocol || 'TCP').toUpperCase();

                            // Per-rule sync check
                            let ruleSynced = false;
                            if (hasNatData && isEnabled && extPort !== '-') {
                              const portNum = typeof extPort === 'string' ? parseInt(extPort, 10) : extPort;
                              ruleSynced = !!dnatRules.find((r: any) => r.external_port === portNum);
                            }

                            return (
                              <div key={s.id} className="text-xs bg-gray-50 rounded p-2">
                                <div className="flex justify-between items-center">
                                  <span className="font-medium text-gray-700">{s.subscription_name || s.name || 'Port Forward'}</span>
                                  {hasNatData && isEnabled ? (
                                    ruleSynced ? (
                                      <span className="px-1.5 py-0.5 text-xs font-medium rounded-full bg-success-100 text-success-700">Synced</span>
                                    ) : (
                                      <span className="px-1.5 py-0.5 text-xs font-medium rounded-full bg-warning-100 text-warning-700 inline-flex items-center space-x-1">
                                        <AlertTriangle className="w-3 h-3" />
                                        <span>Drift</span>
                                      </span>
                                    )
                                  ) : null}
                                </div>
                                <div className="flex justify-between mt-1">
                                  <span className="text-gray-500">Rule:</span>
                                  <span className="font-mono text-gray-700">:{extPort} → {intIp}:{intPort} ({protocol})</span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">VPP Sync</span>
                        {syncStatus === 'synced' ? (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-success-100 text-success-700">Synced</span>
                        ) : syncStatus === 'drift' ? (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-warning-100 text-warning-700 inline-flex items-center space-x-1" title={driftReason}>
                            <AlertTriangle className="w-3 h-3" />
                            <span>Drift</span>
                          </span>
                        ) : (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-600">Unknown</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Other subscriptions as simple cards */}
              {subscriptions
                .filter((s: any) => {
                  const type = (s.type || s.subscription_type || '').toLowerCase();
                  return type !== 'source_nat' && type !== 'dhcp_pool' && type !== 'port_forwarding' && type !== 'port_forward';
                })
                .map((subscription: any) => {
                  const badge = getSubscriptionTypeBadge(subscription.type || subscription.subscription_type);
                  const syncStatus = getSubscriptionSyncStatus(subscription);
                  const hasDrift = syncStatus.status === 'drift';
                  return (
                    <div key={subscription.id} className={`border rounded-lg p-4 ${hasDrift ? 'border-warning-300 bg-warning-50' : 'border-gray-200'}`}>
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">
                          {subscription.subscription_name || subscription.name}
                        </h3>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${badge.color}`}>
                          {badge.label}
                        </span>
                      </div>
                      <div className="space-y-2">
                        {subscription.logicalInterface && (
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-600">Interface</span>
                            <span className="text-sm font-medium text-gray-900">{subscription.logicalInterface}</span>
                          </div>
                        )}
                        {subscription.description && (
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-600">Description</span>
                            <span className="text-sm text-gray-500">{subscription.description}</span>
                          </div>
                        )}
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">VPP Sync</span>
                          {getSubscriptionSyncBadge(subscription)}
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          )}
        </div>
      )}

      {/* 5. Security & VPN Services Section */}
      {showSecuritySection && (
        <div className="card">
          <div className="border-b pb-4 mb-4">
            <h2 className="text-xl font-bold text-gray-900">Security & VPN Services</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Access Control */}
            {showAclCard && (
              <div className="border border-gray-200 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3">Access Control (ACL)</h3>
                {(() => {
                  const aclStatus = vppStatus?.vpp?.acl;
                  const hasAclData = !!aclStatus;
                  const aclCount = aclStatus?.rules?.length || 0;
                  const totalRules = aclStatus?.rules?.reduce((sum: number, acl: any) => sum + (acl.rule_count || acl.entries?.length || 0), 0) || 0;
                  const enabledAcls = aclStatus?.rules?.filter((acl: any) => acl.enabled)?.length || 0;

                  return (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Status</span>
                        {hasAclData ? (
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${aclCount > 0 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                            {aclCount > 0 ? 'Configured' : 'Not Configured'}
                          </span>
                        ) : (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-600">Unknown</span>
                        )}
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">ACLs</span>
                        <span className="text-sm font-medium text-gray-900">{aclCount} ({enabledAcls} enabled)</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Total Rules</span>
                        <span className="text-sm font-medium text-gray-900">{totalRules}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">VPP Sync</span>
                        {hasAclData ? (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-success-100 text-success-700">Synced</span>
                        ) : (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-600">Unknown</span>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Flow Export (IPFIX) */}
            {showIpfixCard && (
              <div className="border border-gray-200 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3">Flow Export (IPFIX)</h3>
                {(() => {
                  // Check database config for actual enabled status
                  const isEnabled = !!flowprobeConfig?.config?.enabled;
                  const plugins = vppStatus?.vpp?.plugins || [];
                  const hasFlowprobe = plugins.some((p: any) => p.name === 'flowprobe_plugin.so');

                  return (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Status</span>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${isEnabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                          {isEnabled ? 'Enabled' : 'Disabled'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Plugin Loaded</span>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${hasFlowprobe ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                          {hasFlowprobe ? 'Yes' : 'No'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">VPP Sync</span>
                        {vppStatus ? (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-success-100 text-success-700">Synced</span>
                        ) : (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-600">Unknown</span>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* WireGuard Server (wg0 - IoT Gateway) */}
            {showWg0Card && (
              <div className="border border-gray-200 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3">WireGuard Server (IoT Gateway)</h3>
                {(() => {
                  const wgStatus = vppStatus?.vpp?.wireguard;
                  const wg0 = wgStatus?.interfaces?.find((i: any) => i.name === 'wg0');
                  const hasWg0 = !!wg0;
                  const peerCount = wg0?.peer_count || 0;
                  const peers = wg0?.peers || [];
                  const connectedPeers = peers.filter((p: any) => p.established).length;
                  const isEnabled = wg0?.enabled;

                  // Drift detection
                  let syncStatus: 'synced' | 'drift' | 'unknown' = 'unknown';
                  if (wgStatus) {
                    if (!wg0) {
                      syncStatus = 'drift';
                    } else if (!isEnabled) {
                      syncStatus = 'drift';
                    } else {
                      syncStatus = 'synced';
                    }
                  }

                  return (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Status</span>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${hasWg0 && isEnabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                          {hasWg0 ? (isEnabled ? 'Enabled' : 'Disabled') : 'Not Configured'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">IoT Gateway Peers</span>
                        <span className="text-sm font-medium text-gray-900">{peerCount}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Connected</span>
                        <span className={`text-sm font-medium ${connectedPeers > 0 ? 'text-green-600' : 'text-gray-500'}`}>
                          {connectedPeers} / {peerCount}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Listen Port</span>
                        <span className="text-sm font-medium text-gray-900">{wg0?.listen_port || '-'}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">VPP Sync</span>
                        {syncStatus === 'synced' ? (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-success-100 text-success-700">Synced</span>
                        ) : syncStatus === 'drift' ? (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-warning-100 text-warning-700 inline-flex items-center space-x-1">
                            <AlertTriangle className="w-3 h-3" />
                            <span>Drift</span>
                          </span>
                        ) : (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-600">Unknown</span>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* WireGuard App Server (wg1 - Mobile/Desktop Apps) */}
            {showWg1Card && (
              <div className="border border-gray-200 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3">WireGuard App Server (VPN Apps)</h3>
                {(() => {
                  const wgStatus = vppStatus?.vpp?.wireguard;
                  const wg1 = wgStatus?.interfaces?.find((i: any) => i.name === 'wg1');
                  const hasWg1 = !!wg1;
                  const peerCount = wg1?.peer_count || 0;
                  const peers = wg1?.peers || [];
                  const connectedPeers = peers.filter((p: any) => p.established).length;
                  const isEnabled = wg1?.enabled;

                  // Drift detection
                  let syncStatus: 'synced' | 'drift' | 'unknown' = 'unknown';
                  if (wgStatus) {
                    if (!wg1) {
                      // wg1 not found but might not be configured - check if it should exist
                      syncStatus = 'unknown';
                    } else if (!isEnabled) {
                      syncStatus = 'drift';
                    } else {
                      syncStatus = 'synced';
                    }
                  }

                  return (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Status</span>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${hasWg1 && isEnabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                          {hasWg1 ? (isEnabled ? 'Enabled' : 'Disabled') : 'Not Configured'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Connected Peers</span>
                        <span className={`text-sm font-medium ${connectedPeers > 0 ? 'text-green-600' : 'text-gray-500'}`}>
                          {connectedPeers} / {peerCount}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Listen Port</span>
                        <span className="text-sm font-medium text-gray-900">{wg1?.listen_port || '-'}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">VPP Sync</span>
                        {syncStatus === 'synced' ? (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-success-100 text-success-700">Synced</span>
                        ) : syncStatus === 'drift' ? (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-warning-100 text-warning-700 inline-flex items-center space-x-1">
                            <AlertTriangle className="w-3 h-3" />
                            <span>Drift</span>
                          </span>
                        ) : (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-600">Unknown</span>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Suricata IDS/IPS */}
            {showSuricataCard && (() => {
              const isConfigured = !!suricataConfig;
              const isEnabled = suricataConfig?.enabled ?? false;
              const mode = suricataConfig?.mode?.toUpperCase() || '-';
              const isRunning = suricataStatus?.running ?? false;
              const uptimeSeconds = suricataStatus?.uptime_seconds || 0;
              const alertsTotal = suricataStatus?.alerts_total || 0;
              const alertsBlocked = suricataStatus?.alerts_blocked || 0;

              // Drift detection: configured + enabled but not running = drift
              const hasSuricataDrift = isConfigured && isEnabled && !isRunning;
              let syncStatus: 'synced' | 'drift' | 'unknown' = 'unknown';
              let driftReason = '';
              if (isConfigured) {
                if (isEnabled && !isRunning) {
                  syncStatus = 'drift';
                  driftReason = 'Suricata is configured and enabled but not running';
                } else if (isEnabled && isRunning) {
                  syncStatus = 'synced';
                } else if (!isEnabled) {
                  syncStatus = 'synced'; // Disabled intentionally
                }
              }

              // Format uptime
              const formatUptime = (seconds: number) => {
                if (!seconds) return '-';
                const hours = Math.floor(seconds / 3600);
                const minutes = Math.floor((seconds % 3600) / 60);
                if (hours > 0) return `${hours}h ${minutes}m`;
                return `${minutes}m`;
              };

              return (
                <div className={`border rounded-lg p-4 ${hasSuricataDrift ? 'border-warning-300 bg-warning-50' : 'border-gray-200'}`}>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Suricata IDS/IPS</h3>
                    <Shield className="w-4 h-4 text-gray-400" />
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Status</span>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${isConfigured && isEnabled
                        ? 'bg-green-100 text-green-700'
                        : isConfigured
                          ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-gray-100 text-gray-600'
                        }`}>
                        {isConfigured ? (isEnabled ? 'Enabled' : 'Disabled') : 'Not Configured'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Mode</span>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${mode === 'IPS'
                        ? 'bg-blue-100 text-blue-700'
                        : mode === 'IDS'
                          ? 'bg-purple-100 text-purple-700'
                          : 'bg-gray-100 text-gray-600'
                        }`}>
                        {mode}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Running</span>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${isRunning ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                        }`}>
                        {isRunning ? 'Yes' : 'No'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Uptime</span>
                      <span className="text-sm font-medium text-gray-900">{formatUptime(uptimeSeconds)}</span>
                    </div>
                    {isEnabled && (
                      <>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Alerts</span>
                          <span className={`text-sm font-medium ${alertsTotal > 0 ? 'text-yellow-600' : 'text-gray-500'}`}>
                            {alertsTotal.toLocaleString()}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Blocked</span>
                          <span className={`text-sm font-medium ${alertsBlocked > 0 ? 'text-red-600' : 'text-gray-500'}`}>
                            {alertsBlocked.toLocaleString()}
                          </span>
                        </div>
                      </>
                    )}
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">VPP Sync</span>
                      {syncStatus === 'synced' ? (
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-success-100 text-success-700">Synced</span>
                      ) : syncStatus === 'drift' ? (
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-warning-100 text-warning-700 inline-flex items-center space-x-1" title={driftReason}>
                          <AlertTriangle className="w-3 h-3" />
                          <span>Drift</span>
                        </span>
                      ) : (
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-600">Unknown</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
};

export default ConfigurationSummary;

